import { API_BASE_URL, DEFAULT_HEADERS } from '../../apiConfig';

/**
 * Tasks Agent Service
 * Helps with task planning, prioritization, and productivity insights
 */
export const TasksAgentService = {
  /**
   * Generate AI-suggested tasks based on learning goals
   * @param {Object} goalData - User's learning goals and context
   * @returns {Promise<Array>} - List of suggested tasks
   */
  async generateTaskSuggestions(goalData) {
    const payload = {
      agentId: 'tasks',
      persona: goalData.persona || null,
      userId: goalData.userId,
      learningGoals: goalData.learningGoals || [],
      currentCourses: goalData.currentCourses || [],
      availableTime: goalData.availableTime || 'flexible',
      preferredDifficulty: goalData.preferredDifficulty || 'medium',
      interests: goalData.interests || [],
      upcomingDeadlines: goalData.upcomingDeadlines || [],
    };

    const response = await fetch(`${API_BASE_URL}/tasks/generate-suggestions`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.suggestedTasks;
  },

  /**
   * Get AI-powered task prioritization recommendations
   * @param {Object} tasksData - Current tasks and context
   * @returns {Promise<Object>} - Prioritization recommendations
   */
  async prioritizeTasks(tasksData) {
    const payload = {
      agentId: 'tasks',
      persona: tasksData.persona || null,
      userId: tasksData.userId,
      tasks: tasksData.tasks.map((task) => ({
        id: task.id,
        what: task.what,
        urgencyLevel: task.urgencyLevel,
        scheduledTime: task.when,
        estimatedDuration: task.estimatedDuration || null,
        learningGoal: task.learningGoal,
        dependencies: task.dependencies || [],
      })),
      userContext: {
        availableTimeToday: tasksData.availableTimeToday || null,
        energyLevel: tasksData.energyLevel || 'medium',
        deadlines: tasksData.deadlines || [],
        learningGoals: tasksData.learningGoals || [],
      },
    };

    const response = await fetch(`${API_BASE_URL}/tasks/prioritize`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      prioritizedTasks: data.prioritizedTasks,
      reasoning: data.reasoning,
      timeAllocation: data.timeAllocation,
      suggestions: data.suggestions,
    };
  },

  /**
   * Break down a complex task into subtasks
   * @param {Object} taskData - Task to break down
   * @returns {Promise<Array>} - List of subtasks
   */
  async breakDownTask(taskData) {
    const payload = {
      agentId: 'tasks',
      persona: taskData.persona || null,
      task: {
        what: taskData.what,
        why: taskData.why,
        learningGoal: taskData.learningGoal,
        difficulty: taskData.difficulty || 'medium',
      },
      userContext: {
        userId: taskData.userId,
        skillLevel: taskData.skillLevel || 'intermediate',
        availableResources: taskData.availableResources || [],
      },
    };

    const response = await fetch(`${API_BASE_URL}/tasks/break-down`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.subtasks;
  },

  /**
   * Get productivity insights and recommendations
   * @param {Object} productivityData - User's task completion data
   * @returns {Promise<Object>} - Productivity analysis
   */
  async getProductivityInsights(productivityData) {
    const payload = {
      agentId: 'tasks',
      persona: productivityData.persona || null,
      userId: productivityData.userId,
      timeframe: productivityData.timeframe || 'week',
      completedTasks: productivityData.completedTasks || [],
      pendingTasks: productivityData.pendingTasks || [],
      taskCompletionTimes: productivityData.taskCompletionTimes || [],
      urgencyDistribution: productivityData.urgencyDistribution || {},
    };

    const response = await fetch(`${API_BASE_URL}/tasks/productivity-insights`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      completionRate: data.completionRate,
      patterns: data.patterns,
      strengths: data.strengths,
      improvements: data.improvements,
      recommendations: data.recommendations,
      optimalWorkTimes: data.optimalWorkTimes,
    };
  },

  /**
   * Generate a smart schedule for tasks
   * @param {Object} scheduleData - Tasks and scheduling constraints
   * @returns {Promise<Object>} - Optimized schedule
   */
  async generateSmartSchedule(scheduleData) {
    const payload = {
      agentId: 'tasks',
      persona: scheduleData.persona || null,
      userId: scheduleData.userId,
      tasks: scheduleData.tasks || [],
      constraints: {
        availableTimeSlots: scheduleData.availableTimeSlots || [],
        preferredWorkHours: scheduleData.preferredWorkHours || [],
        breakPreferences: scheduleData.breakPreferences || {},
        deadlines: scheduleData.deadlines || [],
      },
      preferences: {
        taskBatching: scheduleData.taskBatching || false,
        difficultyDistribution: scheduleData.difficultyDistribution || 'balanced',
        bufferTime: scheduleData.bufferTime || 15,
      },
    };

    const response = await fetch(`${API_BASE_URL}/tasks/generate-schedule`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      schedule: data.schedule,
      reasoning: data.reasoning,
      alternatives: data.alternatives || [],
    };
  },

  /**
   * Chat with Tasks AI Assistant
   * @param {string} userMessage - User's message
   * @param {Object} context - Tasks context and chat history
   * @returns {Promise<Object>} - AI assistant response
   */
  async chatWithTasksAssistant(userMessage, context) {
    const payload = {
      agentId: 'tasks',
      persona: context.persona || null,
      message: userMessage,
      userId: context.userId,
      userName: context.userName || 'Student',
      userProfile: context.userProfile || {},
      chatHistory: context.chatHistory || [],

      // Memory Architecture Layers
      userInstructions: context.userInstructions || null,
      sessionMetadata: context.sessionMetadata || null,
      userMemory: context.userMemory || null,
      conversationSummaries: context.conversationSummaries || null,

      // Task mode: 'active' (working on task) or 'reflection' (completed task)
      mode: context.mode || 'active',
      systemPromptOverride: context.systemPromptOverride || null,

      // Tasks-specific context
      tasksContext: {
        currentTasks: context.currentTasks || [],
        completedToday: context.completedToday || 0,
        pendingUrgent: context.pendingUrgent || 0,
        upcomingDeadlines: context.upcomingDeadlines || [],
        focusedTask: context.focusedTask || null,
      },
    };

    const response = await fetch(`${API_BASE_URL}/tasks/chat`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Clean up the response text - remove excessive whitespace and newlines
    const cleanResponse = (data.response ?? '').replace(/\\n/g, ' ').replace(/\s+/g, ' ').trim();

    return {
      sender: 'Tasks Assistant',
      text: cleanResponse,
      timestamp: new Date().toLocaleTimeString(),
      suggestedActions: (data.suggestedActions || []).map(
        (action) => action.action ?? action.description ?? ''
      ),
      taskRecommendations: (data.taskRecommendations || []).map((rec) => rec.what ?? ''),
    };
  },

  /**
   * Get motivational message based on task progress
   * @param {Object} progressData - Current progress data
   * @returns {Promise<string>} - Motivational message
   */
  async getMotivationalMessage(progressData) {
    const payload = {
      agentId: 'tasks',
      persona: progressData.persona || null,
      userId: progressData.userId,
      completedToday: progressData.completedToday || 0,
      streak: progressData.streak || 0,
      recentAchievements: progressData.recentAchievements || [],
      currentChallenges: progressData.currentChallenges || [],
    };

    const response = await fetch(`${API_BASE_URL}/tasks/motivation`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.message;
  },

  /**
   * AI-powered task creation from natural language
   * @param {Object} taskInput - Natural language task description and context
   * @returns {Promise<Object>} - Structured task with all 5W1H fields
   */
  async createTaskFromNaturalLanguage(taskInput) {
    const payload = {
      agentId: 'tasks',
      persona: taskInput.persona || null,
      userId: taskInput.userId,
      naturalLanguageInput: taskInput.description,
      context: {
        currentGoals: taskInput.currentGoals || [],
        currentCourses: taskInput.currentCourses || [],
        existingTasks: taskInput.existingTasks || [],
        userPreferences: taskInput.userPreferences || {},
      },
    };

    const response = await fetch(`${API_BASE_URL}/tasks/create-from-nl`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      task: data.task, // Structured 5W1H task
      confidence: data.confidence,
      suggestions: data.suggestions || [],
    };
  },

  /**
   * Generate subtasks with AI assistance
   * @param {Object} parentTask - Parent task to break down
   * @param {Object} options - Generation options
   * @returns {Promise<Array>} - List of subtasks
   */
  async generateSubtasks(parentTask, options = {}) {
    const payload = {
      agentId: 'tasks',
      persona: options.persona || null,
      userId: options.userId,
      parentTask: {
        id: parentTask.id,
        what: parentTask.what,
        why: parentTask.why,
        how: parentTask.how,
        urgencyLevel: parentTask.urgencyLevel,
        learningGoal: parentTask.learningGoal,
      },
      options: {
        granularity: options.granularity || 'medium', // fine, medium, coarse
        maxSubtasks: options.maxSubtasks || 10,
        includeTimestamps: options.includeTimestamps !== false,
        userSkillLevel: options.userSkillLevel || 'intermediate',
      },
    };

    const response = await fetch(`${API_BASE_URL}/tasks/generate-subtasks`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.subtasks;
  },

  /**
   * Create or update a goal
   * @param {Object} goalData - Goal information
   * @returns {Promise<Object>} - Created/updated goal
   */
  async manageGoal(goalData) {
    const payload = {
      agentId: 'tasks',
      persona: goalData.persona || null,
      userId: goalData.userId,
      goalId: goalData.goalId || null, // null for new goal
      action: goalData.action || 'create', // create, update, archive
      goal: {
        title: goalData.title,
        description: goalData.description,
        type: goalData.type || 'learning', // learning, personal, career
        category: goalData.category || 'general',
        targetDate: goalData.targetDate || null,
        milestones: goalData.milestones || [],
        metrics: goalData.metrics || [],
        priority: goalData.priority || 'medium',
      },
    };

    const response = await fetch(`${API_BASE_URL}/tasks/manage-goal`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.goal;
  },

  /**
   * Generate tasks from a goal
   * @param {Object} goalData - Goal to generate tasks from
   * @returns {Promise<Array>} - Generated tasks aligned with goal
   */
  async generateTasksFromGoal(goalData) {
    const payload = {
      agentId: 'tasks',
      persona: goalData.persona || null,
      userId: goalData.userId,
      goal: {
        id: goalData.goalId,
        title: goalData.title,
        description: goalData.description,
        targetDate: goalData.targetDate,
        milestones: goalData.milestones || [],
      },
      preferences: {
        timeframe: goalData.timeframe || 'week', // day, week, month
        taskGranularity: goalData.taskGranularity || 'medium',
        urgencyDistribution: goalData.urgencyDistribution || 'balanced',
      },
      context: {
        existingTasks: goalData.existingTasks || [],
        availableTime: goalData.availableTime || null,
        currentProgress: goalData.currentProgress || 0,
      },
    };

    const response = await fetch(`${API_BASE_URL}/tasks/generate-from-goal`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      tasks: data.tasks,
      roadmap: data.roadmap,
      estimatedCompletion: data.estimatedCompletion,
    };
  },

  /**
   * Get goal progress and insights
   * @param {Object} goalQuery - Goal query parameters
   * @returns {Promise<Object>} - Goal progress and insights
   */
  async getGoalProgress(goalQuery) {
    const payload = {
      agentId: 'tasks',
      persona: goalQuery.persona || null,
      userId: goalQuery.userId,
      goalId: goalQuery.goalId,
      includeTaskBreakdown: goalQuery.includeTaskBreakdown !== false,
      timeframe: goalQuery.timeframe || 'all',
    };

    const response = await fetch(`${API_BASE_URL}/tasks/goal-progress`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      progress: data.progress,
      completedTasks: data.completedTasks,
      pendingTasks: data.pendingTasks,
      milestoneProgress: data.milestoneProgress,
      insights: data.insights,
      recommendations: data.recommendations,
      estimatedCompletion: data.estimatedCompletion,
    };
  },

  /**
   * Link tasks to goals automatically
   * @param {Object} linkData - Tasks and goals to analyze
   * @returns {Promise<Object>} - Suggested task-goal mappings
   */
  async suggestTaskGoalLinks(linkData) {
    const payload = {
      agentId: 'tasks',
      persona: linkData.persona || null,
      userId: linkData.userId,
      tasks: linkData.tasks || [],
      goals: linkData.goals || [],
    };

    const response = await fetch(`${API_BASE_URL}/tasks/suggest-goal-links`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      suggestions: data.suggestions,
      unmappedTasks: data.unmappedTasks,
      newGoalSuggestions: data.newGoalSuggestions,
    };
  },

  /**
   * Batch create tasks
   * @param {Object} batchData - Multiple tasks to create
   * @returns {Promise<Object>} - Created tasks with validation
   */
  async batchCreateTasks(batchData) {
    const payload = {
      agentId: 'tasks',
      persona: batchData.persona || null,
      userId: batchData.userId,
      tasks: batchData.tasks,
      options: {
        validateConflicts: batchData.validateConflicts !== false,
        autoSchedule: batchData.autoSchedule || false,
        linkToGoals: batchData.linkToGoals !== false,
      },
    };

    const response = await fetch(`${API_BASE_URL}/tasks/batch-create`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      createdTasks: data.createdTasks,
      conflicts: data.conflicts || [],
      warnings: data.warnings || [],
      goalMappings: data.goalMappings || [],
    };
  },

  /**
   * Get AI recommendations for task optimization
   * @param {Object} optimizationData - Current task state
   * @returns {Promise<Object>} - Optimization recommendations
   */
  async getTaskOptimizationRecommendations(optimizationData) {
    const payload = {
      agentId: 'tasks',
      persona: optimizationData.persona || null,
      userId: optimizationData.userId,
      currentTasks: optimizationData.currentTasks || [],
      goals: optimizationData.goals || [],
      completionHistory: optimizationData.completionHistory || [],
      constraints: optimizationData.constraints || {},
    };

    const response = await fetch(`${API_BASE_URL}/tasks/optimize`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      recommendations: data.recommendations,
      tasksToMerge: data.tasksToMerge || [],
      tasksToSplit: data.tasksToSplit || [],
      tasksToReschedule: data.tasksToReschedule || [],
      tasksToDelegate: data.tasksToDelegate || [],
      reasoning: data.reasoning,
    };
  },
};
